import React, { createContext, useContext, useReducer } from 'react';

import produce from 'immer';

import { buildPolicyState } from 'client/app/apps/policy-library/components/policyInput.helpers';
import {
  addColumn,
  getTableStateFromConfig,
  initDataTable,
  initTableConfig,
} from 'client/app/apps/policy-library/components/ruleTableConfig';
import { validateDialogState } from 'client/app/apps/policy-library/components/validation';
import { RuleMetaKey, RuleMetaType } from 'client/app/apps/policy-library/RulesMeta';
import { LiquidPolicy } from 'client/app/gql';
import { RULE_NAME_FIELD } from 'common/lib/liquidPolicies';
import { SheetState } from 'common/rules/evaluateSpreadsheetRules';
import { CellValue, DataTable } from 'common/types/spreadsheetEditor';
import { TableConfiguration } from 'common/ui/components/Table';

const initState: State = {
  policy: {
    name: {
      value: undefined,
      error: undefined,
    },
    description: {
      value: undefined,
      error: undefined,
    },
    rules: {
      tableConfig: initTableConfig,
      dataTable: initDataTable,
      tableState: getTableStateFromConfig(initTableConfig),
    },
  },
  dialog: {
    mode: 'create',
    open: false,
    activeTab: 0,
    rulesTab: { disabled: true },
    submitButton: { disabled: true },
  },
};

const PolicyDialogStateContext = createContext<[State, React.Dispatch<Action>]>([
  initState,
  () => {
    throw new Error('[CreatePolicyStateContext]: Reducer not implemented');
  },
]);

export default function PolicyDialogStateProvider({
  children,
}: {
  children: React.ReactNode | React.ReactNode[];
}) {
  return (
    <PolicyDialogStateContext.Provider value={useReducer(reducer, initState)}>
      {children}
    </PolicyDialogStateContext.Provider>
  );
}

export const usePolicyDialogState = () => useContext(PolicyDialogStateContext);

export type DialogMode = 'create' | 'edit';

export type State = {
  policy: {
    name: {
      value?: string;
      error?: string;
    };
    description: {
      value?: string;
      error?: string;
    };
    rules: {
      dataTable: DataTable;
      tableConfig: TableConfiguration;
      tableState: SheetState;
    };
  };
  dialog: {
    mode: DialogMode;
    open: boolean;
    activeTab: number;
    rulesTab: { disabled: boolean };
    submitButton: { disabled: boolean; message?: string };
  };
};

type Action =
  | {
      type: 'open_dialog';
      payload: {
        mode: DialogMode;
        policy?: LiquidPolicy;
      };
    }
  | { type: 'close_dialog' }
  | {
      type: 'set_active_tab';
      payload: number;
    }
  | {
      type: 'change_policy_name';
      payload: string | undefined;
    }
  | {
      type: 'change_policy_description';
      payload: string | undefined;
    }
  | {
      type: 'validate_policy_name';
      payload: string | undefined;
    }
  | {
      type: 'validate_policy_description';
    }
  | {
      type: 'update_table';
      payload: DataTable;
    }
  | {
      type: 'add_column';
      payload: {
        name: RuleMetaKey;
        type: RuleMetaType;
      };
    }
  | {
      type: 'add_row';
      payload: number;
    }
  | { type: 'reset_state' };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'open_dialog':
      return produce(state, draft => {
        const { mode, policy } = action.payload;

        draft.dialog = { ...initState.dialog, mode, open: true };

        if (!policy) {
          // Create new policy from scratch
          draft.policy = { ...initState.policy };
        } else if (mode === 'edit') {
          // Edit existing policy
          draft.policy = buildPolicyState(policy);
        } else {
          // Create new policy from existing one
          draft.policy = buildPolicyState(policy);
          draft.policy.name.value += '_Copy';
        }

        draft.dialog = validateDialogState(draft);
      });
    case 'close_dialog':
      return produce(state, draft => {
        draft.dialog.open = false;
      });
    case 'set_active_tab':
      return produce(state, draft => {
        draft.dialog.activeTab = action.payload;
      });
    case 'change_policy_name':
      return produce(state, draft => {
        const name = action.payload;
        draft.policy.name.value = name;
      });
    case 'change_policy_description':
      return produce(state, draft => {
        const description = action.payload;
        draft.policy.description.value = description;
      });
    case 'validate_policy_name':
      return produce(state, draft => {
        const error = action.payload;
        draft.policy.name.error = error;

        draft.dialog = validateDialogState(draft);
      });
    case 'validate_policy_description':
      return produce(state, draft => {
        const description = state.policy.description.value;

        let error: string | undefined;
        if (description?.trim() === '') {
          error =
            'Description cannot be empty. Please, provide a description of your liquid policy.';
        }
        draft.policy.description.error = error;

        draft.dialog = validateDialogState(draft);
      });
    case 'update_table':
      return produce(state, draft => {
        draft.policy.rules.dataTable = action.payload;
        draft.dialog = validateDialogState(draft);
      });
    case 'add_column': {
      const { name, type } = action.payload;

      const table = state.policy.rules.dataTable;

      if (table.schema.fields.some(field => field.name === name)) {
        return state;
      }

      return produce(state, draft => {
        addColumn({
          table: draft.policy.rules.dataTable,
          config: draft.policy.rules.tableConfig,
          column: { type, name },
        });
        const config = draft.policy.rules.tableConfig;
        draft.policy.rules.tableState = getTableStateFromConfig(config);
      });
    }
    case 'add_row':
      return produce(state, draft => {
        const table = draft.policy.rules.dataTable;
        const newRow: Record<string, CellValue> = {};
        const newRowIndex = action.payload + 1;

        table.schema.fields.forEach(field => {
          if (field.name === RULE_NAME_FIELD) {
            newRow[RULE_NAME_FIELD] = `Rule_${newRowIndex}`;
          } else {
            newRow[field.name] = null;
          }
        });
        table.data.push(newRow);
      });
    case 'reset_state':
      return produce(state, draft => {
        draft.policy = initState.policy;
        draft.dialog = initState.dialog;
      });
    default:
      return state;
  }
}
